// Service-worker registration + a tiny event bus for install/update prompts.
// vite-plugin-pwa is configured with `injectRegister: null`, so the SW does
// not auto-register. We do it here so we can:
//   1. Surface "new version available — refresh?" toasts.
//   2. Capture `beforeinstallprompt` for the mobile install banner.

import { registerSW } from 'virtual:pwa-register';

let _deferredPrompt = null;
const _listeners = new Set();

function emit(state) {
  for (const fn of _listeners) {
    try { fn(state); } catch { /* ignore */ }
  }
}

export function onPwaState(fn) {
  _listeners.add(fn);
  // Fire immediately with the current state so the listener sees a snapshot.
  fn({
    canInstall: !!_deferredPrompt,
    needsRefresh: _needsRefresh,
    offlineReady: _offlineReady,
  });
  return () => _listeners.delete(fn);
}

let _needsRefresh = false;
let _offlineReady = false;
let _updateSW = null;

export function initPwa() {
  if (typeof window === 'undefined') return;

  // 1) Service worker
  try {
    _updateSW = registerSW({
      onNeedRefresh() {
        _needsRefresh = true;
        emit({ canInstall: !!_deferredPrompt, needsRefresh: true, offlineReady: _offlineReady });
      },
      onOfflineReady() {
        _offlineReady = true;
        emit({ canInstall: !!_deferredPrompt, needsRefresh: _needsRefresh, offlineReady: true });
      },
    });
  } catch {
    // SW registration is best-effort — the app still works without it.
  }

  // 2) Capture the install prompt
  window.addEventListener('beforeinstallprompt', (e) => {
    // Stop the browser's default mini-infobar; we'll show our own.
    e.preventDefault();
    _deferredPrompt = e;
    emit({ canInstall: true, needsRefresh: _needsRefresh, offlineReady: _offlineReady });
  });

  // 3) Reset state once the app is actually installed
  window.addEventListener('appinstalled', () => {
    _deferredPrompt = null;
    emit({ canInstall: false, needsRefresh: _needsRefresh, offlineReady: _offlineReady });
  });
}

export async function promptInstall() {
  if (!_deferredPrompt) return { outcome: 'unsupported' };
  const event = _deferredPrompt;
  _deferredPrompt = null;
  emit({ canInstall: false, needsRefresh: _needsRefresh, offlineReady: _offlineReady });
  try {
    await event.prompt();
    const choice = await event.userChoice;
    return { outcome: choice?.outcome || 'unknown' };
  } catch {
    return { outcome: 'errored' };
  }
}

export function applyPwaUpdate() {
  if (typeof _updateSW === 'function') {
    _updateSW(true);
  } else {
    window.location.reload();
  }
}

// Heuristics for showing the install banner.
export function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Phones / phablets we want to target. Coarse but good enough.
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

export function isStandalone() {
  if (typeof window === 'undefined') return false;
  // Already running as an installed PWA — don't pester the user.
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
}

export function isIOS() {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent || '');
}
